import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Badge, PageHeader, Modal, Input, Select, Alert } from '../../components/ui';
import api from '../../services/api';
import useAuthStore from '../../store/authStore';
import toast from 'react-hot-toast';

const STATUS_COLOR    = { Open:'success', 'In Progress':'info', Resolved:'warning', Closed:'gray' };
const PRIORITY_COLOR  = { Low:'gray', Medium:'info', High:'warning', Urgent:'danger' };
const CATEGORIES      = ['General','Billing','Technical','Account'];
const PRIORITIES      = ['Low','Medium','High','Urgent'];

export default function Support() {
  const { user } = useAuthStore();
  const [tickets, setTickets]         = useState([]);
  const [selected, setSelected]       = useState(null);
  const [thread, setThread]           = useState(null);
  const [loading, setLoading]         = useState(true);
  const [showNew, setShowNew]         = useState(false);
  const [replyMsg, setReplyMsg]       = useState('');
  const [replying, setReplying]       = useState(false);
  const [form, setForm]               = useState({ subject:'', message:'', category:'General', priority:'Medium' });
  const [saving, setSaving]           = useState(false);
  const threadRef = useRef();

  useEffect(() => { fetchTickets(); }, []);

  useEffect(() => {
    if (selected) fetchThread(selected.id);
  }, [selected]);

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [thread]);

  const fetchTickets = async () => {
    setLoading(true);
    try { const r = await api.get('/tickets'); const sorted = (r.data.data || []).sort((a,b) => new Date(b.lastReplyAt||b.createdAt) - new Date(a.lastReplyAt||a.createdAt)); setTickets(sorted); }
    catch {} finally { setLoading(false); }
  };

  const fetchThread = async (id) => {
    try { const r = await api.get(`/tickets/${id}`); const data = r.data.data;
        if (data && data.replies) {
          data.replies = data.replies.sort((a,b) => new Date(a.createdAt) - new Date(b.createdAt));
        }
        setThread(data); }
    catch { toast.error('Failed to load thread'); }
  };

  const createTicket = async () => {
    if (!form.subject || !form.message) return toast.error('Subject and message required');
    setSaving(true);
    try {
      await api.post('/tickets', form);
      toast.success('Ticket created! We\'ll respond within working hours.');
      setShowNew(false);
      setForm({ subject:'', message:'', category:'General', priority:'Medium' });
      fetchTickets();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const sendReply = async () => {
    if (!replyMsg.trim()) return;
    setReplying(true);
    try {
      await api.post(`/tickets/${selected.id}/reply`, { message: replyMsg });
      setReplyMsg('');
      fetchThread(selected.id);
      fetchTickets();
    } catch (e) { toast.error('Failed to send reply'); }
    finally { setReplying(false); }
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div>
      <PageHeader title="Support" subtitle="Get help from our team · Mon–Sat 10AM–6PM IST" action={<Button onClick={() => setShowNew(true)}>+ New Ticket</Button>} />

      <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: 20, height: 'calc(100vh - 200px)', minHeight: 500 }}>
        {/* ── TICKET LIST ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0, overflow: 'hidden', background: '#111', border: '1px solid rgba(46,204,113,0.1)', borderRadius: 16 }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(46,204,113,0.08)', fontWeight: 700, fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>My Tickets</span>
            <span style={{ fontSize: 12, color: '#555' }}>{tickets.filter(t => t.status === 'Open' || t.status === 'In Progress').length} open</span>
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {loading ? <div style={{ padding: 32, textAlign: 'center', color: '#555' }}>Loading...</div> :
              tickets.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center' }}>
                  <div style={{ fontSize: 36, marginBottom: 10 }}>🎫</div>
                  <div style={{ fontSize: 13, color: '#555' }}>No tickets yet</div>
                  <Button size="sm" style={{ marginTop: 14 }} onClick={() => setShowNew(true)}>+ Create Ticket</Button>
                </div>
              ) : tickets.map(t => (
                <div key={t.id} onClick={() => setSelected(t)} style={{ padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.03)', cursor: 'pointer', background: selected?.id === t.id ? 'rgba(46,204,113,0.08)' : !t.isReadByClient ? 'rgba(46,204,113,0.03)' : 'transparent', borderLeft: `3px solid ${selected?.id === t.id ? '#2ECC71' : 'transparent'}`, transition: 'all 0.15s' }}
                  onMouseEnter={e => { if (selected?.id !== t.id) e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}
                  onMouseLeave={e => { if (selected?.id !== t.id) e.currentTarget.style.background = !t.isReadByClient ? 'rgba(46,204,113,0.03)' : 'transparent'; }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                    <div style={{ fontWeight: !t.isReadByClient ? 800 : 600, fontSize: 13, color: '#fff', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 8 }}>{t.subject}</div>
                    {!t.isReadByClient && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#2ECC71', flexShrink: 0, marginTop: 3 }} />}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <Badge color={STATUS_COLOR[t.status] || 'gray'} style={{ fontSize: 10, padding: '2px 8px' }}>{t.status}</Badge>
                    <Badge color={PRIORITY_COLOR[t.priority] || 'gray'} style={{ fontSize: 10, padding: '2px 8px' }}>{t.priority}</Badge>
                    <Badge color="gray" style={{ fontSize: 10, padding: '2px 8px' }}>{t.category}</Badge>
                  </div>
                  <div style={{ fontSize: 11, color: '#444', marginTop: 6 }}>{t.lastReplyAt ? new Date(t.lastReplyAt).toLocaleString() : new Date(t.createdAt).toLocaleString()}</div>
                </div>
              ))
            }
          </div>
        </div>

        {/* ── THREAD VIEW ── */}
        <div style={{ background: '#111', border: '1px solid rgba(46,204,113,0.1)', borderRadius: 16, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {!selected ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#444', gap: 12 }}>
              <div style={{ fontSize: 52 }}>💬</div>
              <div style={{ fontSize: 14 }}>Select a ticket to view the thread</div>
              <Button size="sm" variant="outline" onClick={() => setShowNew(true)}>+ New Ticket</Button>
            </div>
          ) : (
            <>
              {/* Thread header */}
              <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(46,204,113,0.08)' }}>
                <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 8 }}>{thread?.subject || selected.subject}</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Badge color={STATUS_COLOR[thread?.status || selected.status]}>{thread?.status || selected.status}</Badge>
                  <Badge color={PRIORITY_COLOR[thread?.priority || selected.priority]}>{thread?.priority || selected.priority}</Badge>
                  <Badge color="gray">{thread?.category || selected.category}</Badge>
                  <span style={{ fontSize: 11, color: '#555', alignSelf: 'center' }}>#{selected.id?.slice(0,8).toUpperCase()}</span>
                </div>
              </div>

              {/* Messages */}
              <div ref={threadRef} style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                {!thread ? (
                  <div style={{ textAlign: 'center', padding: 32, color: '#555' }}>Loading...</div>
                ) : (thread.replies || []).map((reply, i) => (
                  <div key={reply.id} style={{ display: 'flex', flexDirection: reply.isAdmin ? 'row' : 'row-reverse', gap: 12, alignItems: 'flex-start' }}>
                    {/* Avatar */}
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: reply.isAdmin ? 'rgba(46,204,113,0.15)' : 'rgba(52,152,219,0.15)', border: `2px solid ${reply.isAdmin ? '#2ECC71' : '#3498DB'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>
                      {reply.isAdmin ? '🛡' : user?.name?.[0]?.toUpperCase()}
                    </div>
                    {/* Bubble */}
                    <div style={{ maxWidth: '75%' }}>
                      <div style={{ fontSize: 11, color: '#555', marginBottom: 4, textAlign: reply.isAdmin ? 'left' : 'right' }}>
                        {reply.isAdmin ? 'MS Discoveries Support' : 'You'} · {new Date(reply.createdAt).toLocaleString()}
                      </div>
                      <div style={{ background: reply.isAdmin ? 'rgba(46,204,113,0.08)' : 'rgba(52,152,219,0.08)', border: `1px solid ${reply.isAdmin ? 'rgba(46,204,113,0.2)' : 'rgba(52,152,219,0.2)'}`, borderRadius: reply.isAdmin ? '4px 14px 14px 14px' : '14px 4px 14px 14px', padding: '12px 16px', fontSize: 13, color: '#ccc', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                        {reply.message}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Reply box */}
              {(thread?.status !== 'Closed') && (
                <div style={{ padding: '14px 20px', borderTop: '1px solid rgba(46,204,113,0.08)' }}>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <textarea value={replyMsg} onChange={e => setReplyMsg(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                      placeholder="Type your message... (Enter to send, Shift+Enter for new line)"
                      rows={3}
                      style={{ flex: 1, padding: '10px 14px', background: '#1a1a1a', border: '1px solid rgba(46,204,113,0.15)', borderRadius: 10, color: '#fff', fontSize: 13, outline: 'none', resize: 'none', fontFamily: 'inherit', lineHeight: 1.6 }} />
                    <Button loading={replying} onClick={sendReply} disabled={!replyMsg.trim()} style={{ alignSelf: 'flex-end' }}>
                      Send →
                    </Button>
                  </div>
                </div>
              )}
              {thread?.status === 'Closed' && (
                <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(46,204,113,0.06)', textAlign: 'center', fontSize: 13, color: '#555' }}>
                  This ticket is closed. <button onClick={() => setShowNew(true)} style={{ background: 'none', border: 'none', color: '#2ECC71', cursor: 'pointer', fontWeight: 600 }}>Open a new ticket</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── NEW TICKET MODAL ── */}
      <Modal open={showNew} onClose={() => setShowNew(false)} title="Create Support Ticket" size="md">
        <Input label="Subject *" placeholder="Brief description of your issue" value={form.subject} onChange={e => f('subject', e.target.value)} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Select label="Category" value={form.category} onChange={e => f('category', e.target.value)}>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </Select>
          <Select label="Priority" value={form.priority} onChange={e => f('priority', e.target.value)}>
            {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
          </Select>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#ccc', marginBottom: 6 }}>Message *</label>
          <textarea value={form.message} onChange={e => f('message', e.target.value)} rows={6}
            placeholder="Describe your issue in detail..."
            style={{ width: '100%', padding: '10px 14px', background: '#1a1a1a', border: '1px solid rgba(46,204,113,0.15)', borderRadius: 10, color: '#fff', fontSize: 13, outline: 'none', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.7 }} />
        </div>
        <Alert type="info" style={{ marginBottom: 16 }}>We respond during working hours: <strong>Monday – Saturday, 10:00 AM – 6:00 PM IST</strong></Alert>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="secondary" onClick={() => setShowNew(false)} style={{ flex: 1 }}>Cancel</Button>
          <Button loading={saving} onClick={createTicket} style={{ flex: 2 }}>Submit Ticket</Button>
        </div>
      </Modal>
    </div>
  );
}
